import type { Maybe } from '@adornis/base/utilTypes.js';
import { confirmDialog, guardDialog } from '@adornis/dialog/dialog-functions.js';
import { downloadLink } from '@adornis/file-utils/client/helper-methods.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import type { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { ListOperationsMenu } from '@martinus/styles/client/menus/ListOperationsMenu.js';
import { BookingAPI } from '../../../../bookings/api/BookingAPI.js';
import { SgSnackbar } from '../../../../client/components/ui/sg-snackbar.js';
import { useMartinusABAC } from '../../../../permissions/client/useMartinusAbac.js';
import { Permission } from '../../../../permissions/roles.js';
import { SepaAPI } from '../../../../sepa/api/SepaAPI.js';
import { acceptAllSepaMandatesForGroup } from '../../../../sepa/client/acceptAllSepaMandatesForGroup.js';
import { openSepaExportDialog } from '../../../../sepa/client/SepaExportDialog.js';
import { FreeMailAdditionalInformation } from '../../../../user-emails/free-text-mails/sendFreeTextMails.js';
import { sendFreeTextEmailDialog } from '../../../../user-emails/free-text-mails/sg-dialog-send-free-mail.js';
import { UserListSelectionInterface } from '../../../../users/list/entities/UserListSelectionInterface.js';
import { UserListActions } from '../../../../users/list/UserListPolicy.js';

const handleAllUserEnd = async (groupId: Maybe<string>) => {
  if (
    !(await confirmDialog(
      'Wirklich alle laufenden Buchungen zum Ende des Tages beenden? Ihre SEPA-Mandate enden mit ihnen und werden danach nicht mehr eingezogen.',
      {
        highlightDecline: true,
        acceptText: 'Alle Buchungen beenden',
        declineText: 'Abbrechen',
      },
    ))
  )
    return;

  await guardDialog(
    async () => {
      if (groupId) return BookingAPI.endAllActive(groupId)();
      return 0;
    },
    {
      success: result => SgSnackbar.show(`${result} Buchungen werden zum Ende des Tages beendet.`),
    },
  );
};

const handleAllUserDelete = async (groupId: Maybe<string>) => {
  if (
    !(await confirmDialog(
      'Wirklich alle laufenden Buchungen löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
      {
        highlightDecline: true,
        acceptText: 'Alle Buchungen löschen.',
        declineText: 'Abbrechen',
      },
    ))
  )
    return;

  await guardDialog(
    async () => {
      if (groupId) return BookingAPI.removeForGroup(groupId)();
      return 0;
    },
    {
      successText: 'Buchungen wurden gelöscht.',
    },
  );
};

const handleAlleSepasReframe = async (groupId: Maybe<string>) => {
  if (
    !(await confirmDialog(
      'Wirklich alle SEPA-Mandate auf die Werte der Gruppe/Buchung zurücksetzen? Diese Aktion kann nicht rückgängig gemacht werden.',
      {
        highlightDecline: true,
        acceptText: 'Alle SEPA-Mandate zurücksetzen.',
        declineText: 'Abbrechen',
      },
    ))
  )
    return;

  await guardDialog(
    async () => {
      if (groupId) return SepaAPI.reframeAllForGroup(groupId)();
      return [];
    },
    {
      success: result => SgSnackbar.show(`${result.length} SEPA-Mandate wurden zurückgesetzt.`),
    },
  );
};

export const GroupDetailMembersListOperations = stateful(
  (options: { groupID: string; groupName: string; currentFilter: Maybe<AdornisFilter>; membersFrozen: boolean }) => {
    const user = useObservable(() => currentUser(), []);

    const { can: canExportSepa } = useMartinusABAC({
      action: UserListActions.USER_LIST_EXPORT_SEPA,
    });

    return ListOperationsMenu({
      sections: [
        {
          title: 'Mitgliedschaften',
          subtext: 'Hier sind die erweiterten Funktionen für die Mitgliedsbearbeitung',
          items: [
            ...(user?.hasPermission(Permission.BOOKING_MASS_END)
              ? [
                  [
                    {
                      label: 'Alle beenden',
                      iconifyClass: 'icon-[material-symbols--align-justify-flex-end]',
                      additionalClasses: 'hover:bg-error/50',
                      onClick: () => handleAllUserEnd(options.groupID),
                    },
                    // a group in billing lets its bookings end, never be deleted - "Freigeben" first
                    ...(options.membersFrozen
                      ? []
                      : [
                          {
                            label: 'Alle löschen',
                            iconifyClass: 'icon-[material-symbols--person-remove-rounded]',
                            additionalClasses: 'hover:bg-error/90',
                            onClick: () => handleAllUserDelete(options.groupID),
                          },
                        ]),
                  ],
                ]
              : []),
          ],
        },
        {
          title: 'Kommunikation',
          items: [
            {
              label: 'E-Mail an Auswahl',
              iconifyClass: 'icon-[material-symbols--mail-rounded]',
              additionalClasses: 'hover:bg-primary/50',
              onClick: async () => {
                if (!options.currentFilter) return;
                // the filter carries the groupID aspect, so the server resolves it as a members selection
                await sendFreeTextEmailDialog(
                  new UserListSelectionInterface({ filter: options.currentFilter }),
                  new FreeMailAdditionalInformation({ membersFilter: options.currentFilter }),
                );
              },
            },
          ],
        },
        {
          title: 'SEPA',
          items: [
            ...(canExportSepa
              ? [
                  {
                    label: 'SEPA-Export',
                    iconifyClass: 'icon-[material-symbols--download-rounded]',
                    additionalClasses: 'hover:bg-neutral/50',
                    onClick: async () => {
                      const settings = await openSepaExportDialog(options.groupID, options.groupName);
                      if (!settings) return;

                      await guardDialog(() => SepaAPI.export(settings)(), {
                        success: async result => {
                          const url = AdornisFile.getDownloadLink(result as string);
                          await downloadLink(url, 'sepa.zip');
                        },
                      });
                    },
                  },
                ]
              : []),
            ...(user?.hasPermission(Permission.ACCEPT_ALL_SEPA_MANDATES_FOR_BILLING_POSITION, ANY_CONTEXT)
              ? [
                  {
                    label: 'Alle SEPA-Mandate akzeptieren',
                    iconifyClass: 'icon-[material-symbols--fact-check]',
                    additionalClasses: 'hover:bg-success/50',
                    onClick: () => acceptAllSepaMandatesForGroup(options.groupID),
                  },
                  {
                    label: 'Alle SEPA-Mandate zurücksetzen',
                    iconifyClass: 'icon-[material-symbols--device-reset]',
                    additionalClasses: 'hover:bg-success/50',
                    onClick: () => handleAlleSepasReframe(options.groupID),
                  },
                ]
              : []),
          ],
        },
      ],
    });
  },
);
